/**
 * MediaLightbox — fullscreen fotoviewer, zelfde UX als de klant-timeline en
 * de office-MediaGrid: scroll-snap-strip (swipen), pijlen/pijltjestoetsen,
 * teller, Esc en klik-naast-de-foto sluiten. Eigen full-screen laag (boven
 * WbModal, z-wb-lightbox), bediening op 52 px en de set-kleuren.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface LightboxFoto {
  id: string;
  url: string;
  /** Onderschrift in de teller-pill, bv. "Foto 3" */
  label: string;
}

interface Props {
  fotos: LightboxFoto[];
  start: number;
  onClose: () => void;
}

export function MediaLightbox({ fotos, start, onClose }: Props) {
  const stripRef = useRef<HTMLDivElement>(null);
  const rustRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(fotos.length - 1, start)));

  // idx is de waarheid bij knop/toets-navigatie; swipes zetten idx bij zodra
  // het scrollen tot rust komt (debounce) — zie de timeline-lightbox.
  const toon = useCallback(
    (i: number, direct = false) => {
      const strip = stripRef.current;
      if (!strip) return;
      const doel = Math.max(0, Math.min(fotos.length - 1, i));
      setIdx(doel);
      strip.scrollTo({ left: doel * strip.clientWidth, behavior: direct ? 'instant' : 'smooth' });
    },
    [fotos.length]
  );

  useEffect(() => {
    toon(start, true);
    const vorige = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const stripIdx = () => {
      const strip = stripRef.current;
      return strip ? Math.round(strip.scrollLeft / strip.clientWidth) : 0;
    };
    const opToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') toon(stripIdx() - 1);
      if (e.key === 'ArrowRight') toon(stripIdx() + 1);
    };
    window.addEventListener('keydown', opToets);
    return () => {
      document.body.style.overflow = vorige;
      window.removeEventListener('keydown', opToets);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opScroll = () => {
    if (rustRef.current) clearTimeout(rustRef.current);
    rustRef.current = setTimeout(() => {
      const strip = stripRef.current;
      if (strip) setIdx(Math.round(strip.scrollLeft / strip.clientWidth));
    }, 90);
  };

  const huidige = fotos[Math.max(0, Math.min(fotos.length - 1, idx))];
  if (!huidige) return null;

  return (
    <div className="fixed inset-0 z-wb-lightbox bg-black/95" role="dialog" aria-modal="true" aria-label="Foto's">
      <button
        type="button"
        onClick={onClose}
        aria-label="Sluiten"
        className="wb-focus fixed z-10 min-h-tap min-w-[52px] rounded-wb bg-white/15 active:bg-white/30 text-white text-3xl leading-none backdrop-blur-sm"
        style={{ top: 'max(env(safe-area-inset-top), 12px)', right: '12px' }}
      >
        &times;
      </button>

      <div
        ref={stripRef}
        onScroll={opScroll}
        className="flex h-full w-full overflow-x-auto snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none' }}
      >
        {fotos.map((f) => (
          <div
            key={f.id}
            onClick={onClose}
            className="flex h-full w-full flex-none snap-center snap-always items-center justify-center px-3 pt-16 pb-20"
          >
            <img
              src={f.url}
              alt={f.label}
              loading="lazy"
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain rounded-wb"
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => toon(idx - 1)}
        aria-label="Vorige foto"
        className="wb-focus hidden md:block fixed left-3 top-1/2 -translate-y-1/2 z-10 min-h-tap min-w-[52px] rounded-wb bg-white/15 active:bg-white/30 text-white text-3xl leading-none backdrop-blur-sm"
      >
        &#8249;
      </button>
      <button
        type="button"
        onClick={() => toon(idx + 1)}
        aria-label="Volgende foto"
        className="wb-focus hidden md:block fixed right-3 top-1/2 -translate-y-1/2 z-10 min-h-tap min-w-[52px] rounded-wb bg-white/15 active:bg-white/30 text-white text-3xl leading-none backdrop-blur-sm"
      >
        &#8250;
      </button>

      <div
        className="fixed left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-wb bg-white/15 text-white font-mono text-sm font-semibold backdrop-blur-sm whitespace-nowrap"
        style={{ bottom: 'max(env(safe-area-inset-bottom), 16px)' }}
      >
        {huidige.label} · {idx + 1} / {fotos.length}
      </div>
    </div>
  );
}
